import { SALESORDERSLINES } from 'constants/Entities';
import { getLiteral } from 'utils/getLiteral';
import { getDecimalsByConfig, DECIMAL_TYPES } from 'utils/decimal';

import UnitOfMeasureReadCell from './UnitOfMeasureReadCell';

/**
 * Get decimals for product lines (from parent sales order context)
 * @param {string} type - DECIMAL_TYPES value
 * @param {string} fieldId - Field ID for maxDecimals lookup
 * @param {Object} salesOrderData - Parent SalesOrder data (optional)
 * @returns {number} Decimals to use
 */
const getLineDecimals = (type, fieldId, salesOrderData) => {
    let idEnvironment = salesOrderData?.idEnvironment || salesOrderData?.idBranch;
    let idCurrency = salesOrderData?.idCurrency;

    if (idEnvironment && typeof idEnvironment === 'object' && idEnvironment.value) {
        idEnvironment = idEnvironment.value;
    }

    if (idCurrency && typeof idCurrency === 'object' && idCurrency.value) {
        idCurrency = idCurrency.value;
    }

    return getDecimalsByConfig({
        type,
        idEnvironment,
        idCurrency,
        entityName: 'SALESORDERSLINES',
        fieldId,
    });
};

/**
 * Returns localeStringOptions for numberCell
 * @param {string} type - DECIMAL_TYPES value
 * @param {string} fieldId - Field ID for maxDecimals lookup
 * @param {Object} salesOrderData - Parent SalesOrder data (optional)
 * @returns {Object} { minimumFractionDigits, maximumFractionDigits }
 */
const getLocaleStringOptions = (type, fieldId, salesOrderData) => {
    const decimals = getLineDecimals(type, fieldId, salesOrderData);
    return {
        maximumFractionDigits: decimals,
        minimumFractionDigits: decimals,
    };
};

const getCalculatedReadOnlyInputParams = () => ({
    // Calculated monetary fields are presentation-only in the grid.
    // Source of truth is OrdersManager recalculation flow.
    isReadOnly: true,
    readOnly: true,
    isDisabled: true,
});

const getNumLinesWidth = (params) => {
    const totalPadding = 40;
    const checkbox = 26;
    const numberWidth = 9;
    if (params?.numLine?.isCrud && params?.data?.length) {
        const num =
            totalPadding + checkbox + Math.abs(params.data.length).toString().length * numberWidth;
        return num;
    } else if (params?.data?.length) {
        const num = totalPadding + Math.abs(params.data.length).toString().length * numberWidth;
        return num;
    } else return 100;
};

const LINE_DISCOUNT_LITERAL_KEYS = {
    1: 'label_salesorder_product_discount1',
    2: 'label_salesorder_product_discount2',
    3: 'label_salesorder_product_discount3',
    4: 'label_salesorder_product_discount4',
};

/**
 * Per-tier columns: % then optional amount (when salesOrderAmountDiscounts). Value col is hidden from field selector (paired with %).
 */
const buildLineDiscountColumns = (params, isSage) => {
    const tiers = isSage ? [1] : [1, 2, 3, 4];
    const showValues = !!params?.salesOrderAmountDiscounts;
    const sym = params?.salesOrderData?.currencySymbol ?? '';

    const cols = [];
    tiers.forEach((n) => {
        const fieldId = `discount${n}`;
        const hideTier = n > 1;
        const discountLabel = getLiteral(LINE_DISCOUNT_LITERAL_KEYS[n]);
        cols.push({
            colId: fieldId,
            fieldSelectorLabel: showValues ? discountLabel : `${discountLabel} (%)`,
            headerName: `${discountLabel} (%)`,
            field: fieldId,
            fieldConfiguration: fieldId,
            cellRenderer: 'numberCell',
            cellRendererParams: {
                align: 'right',
                localeStringOptions: getLocaleStringOptions(
                    DECIMAL_TYPES.GENERAL,
                    fieldId,
                    params?.salesOrderData,
                ),
                inputParams: {
                    limitDecimals: getLineDecimals(
                        DECIMAL_TYPES.GENERAL,
                        fieldId,
                        params?.salesOrderData,
                    ),
                },
            },
            crudType: 'percentage',
            suppressSizeToFit: true,
            width: 90,
            ...(hideTier ? { hide: true } : {}),
            ...(isSage && hideTier ? { alwaysHidden: true } : {}),
        });

        if (showValues) {
            const valueId = `${fieldId}Value`;
            cols.push({
                colId: valueId,
                fieldSelectorLabel: discountLabel,
                headerName: `${discountLabel} (${sym})`,
                field: valueId,
                fieldConfiguration: valueId,
                cellRenderer: 'currencyCell',
                cellRendererParams: {
                    align: 'right',
                    otherFields: {
                        symbol: 'currencySymbol',
                        digits: getLineDecimals(
                            DECIMAL_TYPES.CURRENCY_PRICES,
                            'price',
                            params?.salesOrderData,
                        ),
                        longFormat: true,
                    },
                    inputParams: {
                        limitDecimals: getLineDecimals(
                            DECIMAL_TYPES.CURRENCY_PRICES,
                            'price',
                            params?.salesOrderData,
                        ),
                    },
                },
                crudType: 'currency',
                suppressSizeToFit: true,
                width: 110,
                suppressFromFieldSelector: true,
                ...(hideTier ? { hide: true } : {}),
                ...(isSage && hideTier ? { alwaysHidden: true } : {}),
            });
        }
    });
    return cols;
};

export default (params) => {
    const isSage = !!params?.isSageImplementation;
    const optionalLineDiscountCols = isSage ? [] : ['discount2', 'discount3', 'discount4'];

    const idRelationUoMColumnDefs = params?.uomEnabled
        ? [
              {
                  // UoM column is gated by userData.uomEnabled.
                  // CRUD: ProductLinesTable overrides cellRenderer with UnitOfMeasureCell.
                  // Read-only (detail widget): UnitOfMeasureReadCell — label + inactive warning.
                  // Not visible by default: user turns it on via column selector (cache.visibles).
                  // Placed after quantity (grid order).
                  colId: 'idRelationUoM',
                  field: 'idRelationUoM',
                  fieldConfiguration: 'idRelationUoM',
                  headerName: getLiteral('label_unit_of_measure'),
                  cellRenderer: UnitOfMeasureReadCell,
                  cellRendererParams: {
                      align: 'right',
                  },
                  suppressSizeToFit: true,
                  width: 130,
                  hide: true,
              },
          ]
        : [];

    return {
        onlyFrontendCache: true,
        entity: SALESORDERSLINES,
        columnSelectorSections: [
            {
                label: '',
                fields: [
                    'quantity',
                    ...(params?.uomEnabled ? ['idRelationUoM'] : []),
                    'price',
                    'discount1',
                    ...optionalLineDiscountCols,
                    ...(params?.useTaxesSalesOrders
                        ? ['taxRateId', 'subtotalAmount', 'lineDiscountTotalAmount', 'finalprice']
                        : ['finalprice']),
                ],
            },
        ],
        columnDefs: [
            {
                colId: 'numLine',
                headerName: '#',
                field: 'numLine',
                headerComponent: 'headerTextCell',
                headerComponentParams: {
                    checkbox: {
                        onChange:
                            (!params?.disabledCheckboxes && params?.numLine?.onClickCheckbox) ||
                            null,
                    },
                },
                cellRenderer: 'textCell',
                cellRendererParams: {
                    checkbox: {
                        onChange:
                            (!params?.disabledCheckboxes && params?.numLine?.onClickCheckbox) ||
                            null,
                    },
                },
                pinned: 'left',
                width: getNumLinesWidth(params),
                resizable: false,
                suppressSizeToFit: true,
                rowDrag: true,
            },
            {
                colId: 'description',
                headerName: getLiteral('label_product'),
                field: 'model',
                fieldConfiguration: 'description',
                cellRenderer: 'CarouselReadCell',
                cellRendererParams: {
                    isSquared: true,
                    imageSize: 'big',
                    withEmptyPlaceholder: true,
                    isReadOnly: params?.disabledCheckboxes,
                    otherFields: {
                        rowImageSrc: 'rowImageUrl',
                        rowImagePlaceholder: 'rowImageUrlFallback',
                        secondaryTextKey: 'description',
                        carouselFieldId: 'imageListUrls',
                        carouselImageSrc: 'src',
                        carouselImagePlaceholder: 'fallbackSrc',
                    },
                },
                pinned: 'left',
                width: 250,
                maxWidth: 1000,
                suppressSizeToFit: true,
                crudType: 'carousel',
            },
            {
                colId: 'quantity',
                headerName: getLiteral('title_quantity'),
                field: 'quantity',
                fieldConfiguration: 'quantity',
                cellRenderer: 'numberCell',
                crudType: 'decimal',
                suppressSizeToFit: true,
                width: 120,
                cellRendererParams: {
                    align: 'right',
                    localeStringOptions: getLocaleStringOptions(
                        DECIMAL_TYPES.QUANTITY,
                        'quantity',
                        params?.salesOrderData,
                    ),
                    inputParams: {
                        limitDecimals: getLineDecimals(
                            DECIMAL_TYPES.QUANTITY,
                            'quantity',
                            params?.salesOrderData,
                        ),
                    },
                },
            },
            ...idRelationUoMColumnDefs,
            {
                colId: 'price',
                headerName: getLiteral('label_unitprice'),
                field: 'price',
                fieldConfiguration: 'price',
                cellRenderer: 'currencyCell',
                cellRendererParams: {
                    align: 'right',
                    otherFields: {
                        symbol: 'currencySymbol',
                        digits: getLineDecimals(
                            DECIMAL_TYPES.CURRENCY_PRICES,
                            'price',
                            params?.salesOrderData,
                        ),
                        longFormat: true,
                    },
                    inputParams: {
                        limitDecimals: getLineDecimals(
                            DECIMAL_TYPES.CURRENCY_PRICES,
                            'price',
                            params?.salesOrderData,
                        ),
                    },
                },
                crudType: 'currency',
                suppressSizeToFit: true,
                width: 120,
            },
            ...buildLineDiscountColumns(params, isSage),
            ...(params?.useTaxesSalesOrders
                ? [
                      // CRUD: syncStatusEnabled / schema readOnly / lines locked → textCell (plain text)
                      // instead of read-only Select; otherwise Select in ProductLinesTable.
                      {
                          colId: 'taxRateId',
                          headerName: getLiteral('label_products_default_tax'),
                          field: 'taxRateId',
                          cellRenderer: 'textCell',
                          crudType: 'singleValueList',
                          suppressSizeToFit: true,
                          width: 130,
                      },
                      {
                          colId: 'subtotalAmount',
                          headerName: getLiteral('label_subtotal'),
                          field: 'subtotalAmount',
                          cellRenderer: 'currencyCell',
                          cellRendererParams: {
                              align: 'right',
                              otherFields: {
                                  symbol: 'currencySymbol',
                                  digits: getLineDecimals(
                                      DECIMAL_TYPES.CURRENCY_PRICES,
                                      'subtotalAmount',
                                      params?.salesOrderData,
                                  ),
                                  longFormat: true,
                              },
                              inputParams: {
                                  ...getCalculatedReadOnlyInputParams(),
                              },
                          },
                          suppressSizeToFit: true,
                          width: 130,
                      },
                      {
                          colId: 'lineDiscountTotalAmount',
                          headerName: getLiteral('label_salesorder_line_total_discounts'),
                          field: 'lineDiscountTotalAmount',
                          cellRenderer: 'currencyCell',
                          cellRendererParams: {
                              align: 'right',
                              otherFields: {
                                  symbol: 'currencySymbol',
                                  digits: getLineDecimals(
                                      DECIMAL_TYPES.CURRENCY_PRICES,
                                      'subtotalAmount',
                                      params?.salesOrderData,
                                  ),
                                  longFormat: true,
                              },
                              inputParams: {
                                  ...getCalculatedReadOnlyInputParams(),
                              },
                          },
                          suppressSizeToFit: true,
                          width: 130,
                      },
                      {
                          colId: 'finalprice',
                          headerName: getLiteral('label_total'),
                          field: 'finalprice',
                          cellRenderer: 'currencyCell',
                          cellRendererParams: {
                              align: 'right',
                              otherFields: {
                                  symbol: 'currencySymbol',
                                  digits: getLineDecimals(
                                      DECIMAL_TYPES.CURRENCY_PRICES,
                                      'finalPrice',
                                      params?.salesOrderData,
                                  ),
                                  longFormat: true,
                              },
                              inputParams: {
                                  ...getCalculatedReadOnlyInputParams(),
                                  limitDecimals: getLineDecimals(
                                      DECIMAL_TYPES.CURRENCY_PRICES,
                                      'finalPrice',
                                      params?.salesOrderData,
                                  ),
                              },
                          },
                          suppressSizeToFit: true,
                          width: 130,
                      },
                  ]
                : [
                      {
                          colId: 'finalprice',
                          headerName: getLiteral('label_total'),
                          field: 'finalprice',
                          cellRenderer: 'currencyCell',
                          cellRendererParams: {
                              align: 'right',
                              otherFields: {
                                  symbol: 'currencySymbol',
                                  digits: getLineDecimals(
                                      DECIMAL_TYPES.CURRENCY_PRICES,
                                      'finalPrice',
                                      params?.salesOrderData,
                                  ),
                                  longFormat: true,
                              },
                              inputParams: {
                                  ...getCalculatedReadOnlyInputParams(),
                                  limitDecimals: getLineDecimals(
                                      DECIMAL_TYPES.CURRENCY_PRICES,
                                      'finalPrice',
                                      params?.salesOrderData,
                                  ),
                              },
                          },
                          suppressSizeToFit: true,
                      },
                  ]),
        ],
    };
};
